import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';

import { typeLabel } from '../utils/entityLabels';

function AgentView({ filters, loading, setLoading }) {
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  const [agentDetail, setAgentDetail] = useState({}); // { [agent_id]: { entities, recordings, loading } }

  // Build URL params from filters. The FilterBar only exposes Date From/To and Region
  // — time_range comes from the global default or org-settings (often a full year),
  // which would bin the recordings chart by month. Pin the rolling window to 30 days
  // on this page so "no filter" means "last month by day".
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    const hasAbsoluteRange = filters.date_from || filters.date_to;
    Object.entries(filters).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      if (k === 'time_range') return;
      params.append(k, v);
    });
    if (!hasAbsoluteRange) {
      params.append('time_range', '30');
    }
    return params;
  }, [filters]);

  useEffect(() => {
    setStatsLoading(true);
    const params = buildParams();
    axios.get(`/api/agent-view/stats?${params}`)
      .then(res => setStats(res.data?.data || []))
      .catch(() => {})
      .finally(() => { setStatsLoading(false); setLoading(false); });
    // Reset any open expansion when filters change — the cached per-agent data is
    // tied to the old filter window.
    setExpandedAgentId(null);
    setAgentDetail({});
  }, [filters, buildParams, setLoading]);

  const loadAgentDetail = useCallback((agentId) => {
    setAgentDetail(prev => ({ ...prev, [agentId]: { ...(prev[agentId] || {}), loading: true } }));
    const params = buildParams();
    params.append('agent_id', agentId);
    const url = (path) => `/api/agent-view/${path}?${params}`;
    Promise.all([
      axios.get(url('agent-entities')).then(r => r.data || {}).catch(() => ({})),
      axios.get(url('agent-recordings')).then(r => r.data?.data || []).catch(() => []),
    ]).then(([entitiesRes, recordings]) => {
      setAgentDetail(prev => ({
        ...prev,
        [agentId]: {
          entities: entitiesRes.data || [],
          availableTypes: entitiesRes.available_types || [],
          entityType: '',
          recordings,
          loading: false,
        },
      }));
    });
  }, [buildParams]);

  // The type filter is a SQL predicate (the histogram is capped at 30 rows, so filtering
  // client-side over an already-truncated list would hide minority types), which means a
  // type change needs a request. Only the histogram is refetched — recordings per day do
  // not depend on entity type.
  const changeAgentEntityType = useCallback((agentId, entityType) => {
    setAgentDetail(prev => ({ ...prev, [agentId]: { ...(prev[agentId] || {}), entityType, entitiesLoading: true } }));
    const params = buildParams();
    params.append('agent_id', agentId);
    if (entityType) params.append('entity_type', entityType);
    axios.get(`/api/agent-view/agent-entities?${params}`)
      .then(r => setAgentDetail(prev => ({
        ...prev,
        [agentId]: {
          ...(prev[agentId] || {}),
          entities: r.data?.data || [],
          // A narrowed request still returns every type, but keep the previous list on a
          // partial response so the picker cannot lose its options mid-session.
          availableTypes: r.data?.available_types || prev[agentId]?.availableTypes || [],
          entitiesLoading: false,
        },
      })))
      .catch(() => setAgentDetail(prev => ({
        ...prev, [agentId]: { ...(prev[agentId] || {}), entitiesLoading: false },
      })));
  }, [buildParams]);

  const toggleAgent = (agentId) => {
    if (expandedAgentId === agentId) {
      setExpandedAgentId(null);
      return;
    }
    setExpandedAgentId(agentId);
    if (!agentDetail[agentId]) {
      loadAgentDetail(agentId);
    }
  };

  return (
    <div>
      <FilterBar />

      <Panel title="Agent View" subtitle="Click an agent row to see their entity mentions (counted once per conversation) and recordings per day" loading={statsLoading}>
        {stats.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                  <th style={thStyle}></th>
                  <th style={thStyle}>Agent</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Conversations</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Entities / Conv</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Avg Length (chars)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Avg Words</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Audio Length (avg / total min)</th>
                </tr>
              </thead>
              <tbody>
                {stats.map(row => {
                  const isOpen = expandedAgentId === row.agent_id;
                  const detail = agentDetail[row.agent_id];
                  return (
                    <React.Fragment key={row.agent_id}>
                      <tr
                        onClick={() => toggleAgent(row.agent_id)}
                        style={{
                          borderBottom: '1px solid var(--card-border)',
                          cursor: 'pointer',
                          background: isOpen ? 'var(--card-bg-hover, rgba(31,94,255,0.04))' : 'transparent',
                        }}
                      >
                        <td style={{ ...tdStyle, width: '24px', color: 'var(--text-lighter)' }}>
                          {isOpen ? '▾' : '▸'}
                        </td>
                        <td style={tdStyle}>{row.agent_name}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.conversation_count}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.entities_per_conversation ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avg_conversation_length != null ? Math.round(row.avg_conversation_length).toLocaleString() : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avg_word_count != null ? Math.round(row.avg_word_count).toLocaleString() : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {row.avg_audio_minutes != null
                            ? `${row.avg_audio_minutes.toLocaleString()} / ${row.total_audio_minutes.toLocaleString()}`
                            : '—'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                          <td colSpan={7} style={{ padding: '16px 14px 24px', background: 'var(--card-bg-hover, rgba(0,0,0,0.015))' }}>
                            <AgentDetail
                              detail={detail}
                              agentName={row.agent_name}
                              filters={filters}
                              onEntityTypeChange={t => changeAgentEntityType(row.agent_id, t)}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-lighter)', fontSize: '14px' }}>
            No agent data available for the selected filters.
          </div>
        )}
      </Panel>
    </div>
  );
}

function toISODate(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function recordingsRange(filters) {
  const today = new Date();
  const toISO = filters.date_to || today.toISOString().slice(0, 10);
  const fromISO = filters.date_from
    || new Date(today.getTime() - 30 * 86400000).toISOString().slice(0, 10);
  return [fromISO, toISO];
}

function AgentDetail({ detail, agentName, filters, onEntityTypeChange }) {
  if (!detail || detail.loading) {
    return <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>Loading…</div>;
  }
  const {
    entities = [], recordings = [], availableTypes = [], entityType = '', entitiesLoading = false,
  } = detail;
  const chartLayout = {
    margin: { t: 30, r: 20, b: 80, l: 60 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { family: 'Inter, sans-serif', color: 'var(--text-dark)' },
  };
  const chartConfig = { displayModeBar: false, responsive: true };
  const [xMin, xMax] = recordingsRange(filters);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <div style={{ ...miniTitleStyle, marginBottom: 0 }}>
            Entities mentioned by {agentName}
          </div>
          {availableTypes.length > 0 && (
            <select
              aria-label="Entity type"
              value={entityType}
              onChange={e => onEntityTypeChange(e.target.value)}
              style={{
                padding: '4px 10px', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                border: '1px solid var(--card-border)', borderRadius: '8px',
                background: 'var(--card-bg)', color: 'var(--text-dark)',
              }}
            >
              <option value="">All types</option>
              {availableTypes.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
            </select>
          )}
        </div>
        {entitiesLoading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>Loading…</div>
        ) : entities.length > 0 ? (
          <Plot
            data={[{
              x: entities.map(d => d.entity_name),
              y: entities.map(d => Number(d.mention_count)),
              type: 'bar',
              marker: { color: '#1f5eff' },
            }]}
            layout={{
              ...chartLayout,
              xaxis: { title: 'Entity', tickangle: -45, automargin: true },
              yaxis: { title: 'Mention Count' },
            }}
            config={chartConfig}
            useResizeHandler
            style={{ width: '100%', height: '360px' }}
          />
        ) : (
          <div style={emptyStyle}>No entity mentions in this time range.</div>
        )}
      </div>

      <div>
        <div style={miniTitleStyle}>Recordings per day</div>
        {recordings.length > 0 ? (
          <Plot
            data={[{
              x: recordings.map(d => toISODate(d.day)),
              y: recordings.map(d => Number(d.recordings)),
              type: 'bar',
              marker: { color: '#c98b12' },
            }]}
            layout={{
              ...chartLayout,
              xaxis: {
                title: 'Date',
                type: 'date',
                range: [xMin, xMax],
                tickformat: '%b %d',
                automargin: true,
              },
              yaxis: { title: 'Recordings', rangemode: 'tozero', tickformat: 'd' },
            }}
            config={chartConfig}
            useResizeHandler
            style={{ width: '100%', height: '320px' }}
          />
        ) : (
          <div style={emptyStyle}>No recordings in this time range.</div>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: '12px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 900,
  textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--text-lighter)',
};
const tdStyle = {
  padding: '12px 14px', color: 'var(--text-dark)',
};
const miniTitleStyle = {
  fontSize: '11px', fontWeight: 800, textTransform: 'uppercase',
  letterSpacing: '0.18em', color: 'var(--text-lighter)', marginBottom: '8px',
};
const emptyStyle = {
  textAlign: 'center', padding: '24px', color: 'var(--text-lighter)', fontSize: '14px',
};

export default AgentView;
